import {MessageCircle} from 'lucide-react'
import React, {useEffect, useState} from 'react'
import {useIntl} from 'react-intl'
import {useLocation} from 'react-router'

import {ChatbotMode} from './components/config/chatbotModeConfig'
import AttendanceBotAvatar from './components/ui/AttendanceBotAvatar'

/** Equal gap from the right and bottom edges, matching the open widget's offset. */
const LAUNCHER_EDGE_GAP = '28px'

/**
 * Body classes other fixed-corner UI (the Metronic scroll-to-top button) reads to move
 * out from under the launcher — see the `.scrolltop` overrides in _aichatbot.scss.
 */
const BODY_CLASS_LAUNCHER_VISIBLE = 'chatbot-launcher-visible'
const BODY_CLASS_LAUNCHER_LIFTED = 'chatbot-launcher-lifted'

interface ChatbotTriggerProps {
  onClick: () => void
  unreadCount: number
  mode?: ChatbotMode
  /** Draws attention to the launcher the first time a user sees it. */
  highlight?: boolean
}

const ChatbotTrigger: React.FC<ChatbotTriggerProps> = ({
  onClick,
  unreadCount,
  mode = 'general',
  highlight = false,
}) => {
  const intl = useIntl()
  // The unified assistant (attendance and/or support boards) is the mascot launcher named
  // "Versi AI Assistant"; only the generic bot keeps the glyph and its own labels.
  const showsMascot = mode === 'attendance' || mode === 'faq'
  const ariaLabelId = showsMascot
    ? 'CHATBOT.TRIGGER_ARIA_LABEL_ASSISTANT'
    : 'CHATBOT.TRIGGER_ARIA_LABEL'
  const titleId = showsMascot ? 'CHATBOT.TRIGGER_TITLE_ASSISTANT' : 'CHATBOT.TRIGGER_TITLE'
  const [hasTutorialButton, setHasTutorialButton] = useState(false)
  const {pathname} = useLocation()
  // Only the dashboard gets the ambient glow: that is where the feature banner
  // introduces the assistant, so the launcher is emphasised to match.
  const emphasize = pathname === '/dashboard'

  useEffect(() => {
    // Check if tutorial button (walkthrough-help-icon) exists in the DOM
    const checkTutorialButton = () => {
      const tutorialButton = document.querySelector('.walkthrough-help-icon')
      setHasTutorialButton(!!tutorialButton)
    }

    // Initial check
    checkTutorialButton()

    // Watch for changes using MutationObserver
    const observer = new MutationObserver(checkTutorialButton)
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    })

    return () => {
      observer.disconnect()
    }
  }, [])

  useEffect(() => {
    const {classList} = document.body
    classList.add(BODY_CLASS_LAUNCHER_VISIBLE)
    classList.toggle(BODY_CLASS_LAUNCHER_LIFTED, hasTutorialButton)
    return () => {
      classList.remove(BODY_CLASS_LAUNCHER_VISIBLE, BODY_CLASS_LAUNCHER_LIFTED)
    }
  }, [hasTutorialButton])

  return (
    <div
      className='position-fixed chatbot-trigger'
      style={{
        zIndex: 150,
        // Sits the same distance from the right and bottom edges as the open panel; only lifts
        // clear when the tutorial button occupies that corner.
        bottom: hasTutorialButton ? '92px' : LAUNCHER_EDGE_GAP,
        right: LAUNCHER_EDGE_GAP,
      }}
    >
      {highlight && (
        <span className='chatbot-trigger-badge'>
          {intl.formatMessage({id: 'CHATBOT.TRIGGER_NEW_BADGE'})}
        </span>
      )}

      <div className='chatbot-trigger__row'>
        {emphasize && <span className='chatbot-trigger-glow' aria-hidden='true' />}

        {highlight && (
          <>
            <span className='chatbot-trigger-pulse' aria-hidden='true' />
            <span className='chatbot-trigger-halo' aria-hidden='true' />
          </>
        )}

        {/* Says what the mascot IS. The icon alone does not read as an AI assistant to somebody
            seeing it for the first time, and a launcher nobody recognises is a launcher nobody
            clicks. Kept deliberately quiet — it sits over the dashboard all day. */}
        {showsMascot && (
          <span className='chatbot-trigger-identity' aria-hidden='true'>
            <span className='chatbot-trigger-identity__name'>
              {intl.formatMessage({id: 'CHATBOT.TRIGGER_IDENTITY_NAME'})}
            </span>
            <span className='chatbot-trigger-identity__role'>
              {intl.formatMessage({id: 'CHATBOT.TRIGGER_IDENTITY_ROLE'})}
            </span>
          </span>
        )}

        <button
          type='button'
          onClick={(event) => {
            event.preventDefault()
            event.stopPropagation()
            onClick()
          }}
          className={`btn btn-primary rounded-circle shadow chatbot-trigger-btn d-flex justify-content-center align-items-center${
            showsMascot ? ' chatbot-trigger-btn--attendance' : ''
          }`}
          style={{
            width: '60px',
            height: '60px',
          }}
          aria-label={intl.formatMessage({id: ariaLabelId})}
          title={intl.formatMessage({id: titleId})}
        >
          {/* Own clipping layer (like the shimmer) so the overhanging unread badge is not cut off. */}
          {showsMascot && (
            <span className='chatbot-trigger-avatar' aria-hidden='true'>
              <AttendanceBotAvatar />
            </span>
          )}

          {/* Recurring light sweep — repeats every 10s for as long as the launcher is on screen,
              unlike `chatbot-trigger-pulse` which only runs before the first open. */}
          <span className='chatbot-trigger-shimmer' aria-hidden='true' />

          {!showsMascot && (
            <MessageCircle
              size={48}
              color='white'
              style={{
                width: '32px',
                height: '32px',
                minWidth: '32px',
                minHeight: '32px',
              }}
            />
          )}

          {unreadCount > 0 && (
            <span className='position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger'>
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>
      </div>
    </div>
  )
}

export default ChatbotTrigger
